import React from 'react';
import {
  MarketingLayout,
  Section,
  Hero,
  SplitContent,
  FeatureGrid,
  CallToAction,
} from '@byscott-io/corebyscott';

const billingFeatures = [
  {
    icon: '💰',
    title: 'Flat Rate',
    description: 'Simple fixed pricing for unlimited users or usage.',
  },
  {
    icon: '👥',
    title: 'Per User',
    description: 'Charge based on the number of seats or users.',
  },
  {
    icon: '📊',
    title: 'Usage Based',
    description: 'Bill customers based on actual consumption.',
  },
  {
    icon: '📈',
    title: 'Tiered Pricing',
    description: 'Volume discounts that scale with usage.',
  },
];

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      <Hero
        headline="Powerful Features"
        subheadline="Everything you need to manage billing across your SaaS portfolio"
        size="small"
      />

      <Section>
        <SplitContent
          title="Multi-App Management"
          description="Manage billing for all your SaaS applications from a single dashboard. Each app gets its own API key, products, and pricing configuration while sharing a unified billing infrastructure."
          features={[
            'Separate API keys per application',
            'Shared customer management',
            'Unified reporting and analytics',
            'Centralized Square integration',
          ]}
          imagePosition="right"
          image={
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-8 text-white">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📱</span>
                  <span className="font-medium">ChurchCare</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎫</span>
                  <span className="font-medium">IssuesByScott</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💵</span>
                  <span className="font-medium">FinanceByScott</span>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">Flexible Billing Models</h2>
          <p className="mt-4 text-lg text-gray-600">
            Choose the pricing model that fits your business
          </p>
        </div>
        <FeatureGrid features={billingFeatures} columns={4} />
      </Section>

      <Section>
        <SplitContent
          title="Entitlement System"
          description="Control exactly what features each customer can access. Our powerful entitlement system supports boolean features, numeric quotas, and JSON configuration settings."
          features={[
            'Feature flags per subscription level',
            'Numeric quotas with usage tracking',
            'JSON settings for complex configuration',
            'Per-subscription overrides',
          ]}
          imagePosition="left"
          image={
            <div className="bg-gray-900 rounded-lg p-6 text-green-400 font-mono text-sm">
              <pre>{`// Check feature access
const { hasFeature } = useBilling();

if (hasFeature('advanced_reporting')) {
  // Show advanced reports
}

// Check quota usage
const { checkQuota } = useBilling();
const { allowed, remaining } =
  checkQuota('api_calls');`}</pre>
            </div>
          }
        />
      </Section>

      <Section background="gray">
        <SplitContent
          title="Square Integration"
          description="Connect your Square account to process payments securely. Square handles customer cards and payments while BillingByScott manages all subscription logic."
          features={[
            'Secure card storage via Square',
            'PCI-compliant payment processing',
            'Sandbox and production environments',
            'Simple credential configuration',
          ]}
          imagePosition="right"
          image={
            <div className="bg-white rounded-lg shadow-lg p-6 border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">💳</span>
                </div>
                <div>
                  <div className="font-semibold">Square</div>
                  <div className="text-sm text-green-600">Connected</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Environment</span>
                  <span className="text-green-600">Production</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="text-green-600">Active</span>
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <Section>
        <SplitContent
          title="Trials & Prorations"
          description="Offer free trials to attract new customers. When customers upgrade or downgrade, prorations are calculated automatically to ensure fair billing."
          features={[
            'Configurable trial periods per level',
            'Automatic trial expiration handling',
            'Proration preview before changes',
            'Credit application on downgrades',
          ]}
          imagePosition="left"
          image={
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg p-6">
              <div className="text-center mb-4">
                <span className="text-4xl">🎁</span>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">14-Day Trial</div>
                <div className="text-gray-600 mt-1">No credit card required</div>
                <div className="mt-4 inline-block bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  Start Free Trial
                </div>
              </div>
            </div>
          }
        />
      </Section>

      <CallToAction
        headline="Ready to get started?"
        subheadline="Set up your billing infrastructure in minutes, not months."
        primaryCta={{ label: 'Start Free Trial', href: '/register' }}
        secondaryCta={{ label: 'View Pricing', href: '/pricing' }}
        background="indigo"
      />
    </MarketingLayout>
  );
}
